import { formatarAteDuasCasas, formatarDataHora } from '@/lib/formato';

import type { LeiturasDoTurno } from './api';

/**
 * O MEDIDOR DO TURNO (decisão 51, migração 0113): o caminhão aponta turno
 * como qualquer máquina, e as duas leituras do turno estão na unidade do
 * medidor dela — horas no horímetro, quilômetros no odômetro.
 *
 * Mora aqui, sem tela, para a mesa e o celular dizerem a MESMA coisa e o
 * teste os alcançar. A hora de motor só existe no horímetro: no odômetro o
 * banco a deixa nula, e a tela mostra o quilômetro rodado no lugar dela.
 */
export type TipoDoMedidor = 'horimetro' | 'odometro';

/**
 * O absurdo por turno, pela unidade. É o mesmo número da rota
 * (`TETO_DO_MEDIDOR_NO_TURNO` em `backend/src/rotas/operacao.ts`) e do banco
 * (`apontamento_teto_do_medidor_no_turno`, 0113); o teste confere os três.
 */
export const TETO_DO_MEDIDOR_NO_TURNO: Readonly<Record<TipoDoMedidor, number>> = { horimetro: 24, odometro: 2000 };

export interface Medidor {
  readonly tipo: TipoDoMedidor;
  /** "Horímetro" ou "Odômetro", para o rótulo do campo. */
  readonly nome: 'Horímetro' | 'Odômetro';
  /** "horímetro" ou "odômetro", para o meio da frase. */
  readonly noMeio: 'horímetro' | 'odômetro';
  readonly unidade: 'h' | 'km';
  readonly teto: number;
}

/** O medidor de um tipo vindo da API. Nulo ou desconhecido é horímetro, a herança padrão do cadastro (0006). */
export function medidorDo(tipo: string | null | undefined): Medidor {
  return tipo === 'odometro'
    ? { tipo: 'odometro', nome: 'Odômetro', noMeio: 'odômetro', unidade: 'km', teto: TETO_DO_MEDIDOR_NO_TURNO.odometro }
    : { tipo: 'horimetro', nome: 'Horímetro', noMeio: 'horímetro', unidade: 'h', teto: TETO_DO_MEDIDOR_NO_TURNO.horimetro };
}

/**
 * O que está errado entre as duas leituras do turno, ou nulo. A mesma frase
 * na mesa, na correção e no celular.
 */
export function problemaDasLeituras(tipo: string | null | undefined, inicial: number, final: number): string | null {
  const m = medidorDo(tipo);
  if (final < inicial) return `O ${m.noMeio} do fim não pode ser menor que o do início.`;
  if (final - inicial > m.teto) {
    return `O ${m.noMeio} andou mais de ${formatarAteDuasCasas(m.teto)} ${m.unidade} num turno. Confira as duas leituras: um dígito a mais é o erro mais comum.`;
  }
  return null;
}

/**
 * Quanto o medidor andou no turno, para mostrar: as horas de motor no
 * horímetro (a coluna gerada do banco), os quilômetros no odômetro. Leitura
 * da API chega como texto: sempre `Number(...)`, nunca a string crua.
 */
export function andamentoDoTurno(a: {
  readonly tipoMedidor?: string | null;
  readonly horimetroInicial: string | number;
  readonly horimetroFinal: string | number;
  readonly horasMotor: string | number | null;
}): { readonly valor: number | null; readonly unidade: 'h' | 'km' } {
  const m = medidorDo(a.tipoMedidor);
  if (m.tipo === 'odometro') {
    const km = Number(a.horimetroFinal) - Number(a.horimetroInicial);
    return { valor: Number.isFinite(km) ? Math.round(km * 100) / 100 : null, unidade: 'km' };
  }
  return { valor: a.horasMotor === null ? null : Number(a.horasMotor), unidade: 'h' };
}

/**
 * O MEDIDOR INICIAL SUGERIDO DO TURNO (achado dos robos no QAS, 20/10/2026):
 * a ultima leitura ANTES do fim deste turno, e nao a ultima da maquina — o
 * turno da manha apontado depois do da tarde vinha com o horimetro da tarde.
 * Sem a resposta do servidor (sem rede), fica a ultima leitura conhecida,
 * como antes; com a resposta e sem leitura anterior, o campo fica vazio.
 */
export function medidorInicialSugerido(
  leituras: LeiturasDoTurno | undefined,
  ultima: { readonly valor: number } | null | undefined,
  /* A resposta dos intervalos novos ainda vem (rodada 3): a anterior e de
     outro fim de turno, e a ultima da maquina pode ser posterior a ele. Nada
     e sugerido ate chegar — sugerir o numero errado e o 422 de depois. */
  esperando = false,
): number | null {
  if (esperando) return null;
  if (leituras === undefined) return ultima?.valor ?? null;
  return leituras.antes?.valor ?? null;
}

/**
 * O FINAL QUE PASSA DA LEITURA SEGUINTE: o banco recusa a leitura maior que
 * uma registrada depois dela (0119). A tela diz antes, com a leitura, a hora e
 * o que conferir — e o botao nao envia o que vai voltar recusado.
 */
export function problemaComALeituraSeguinte(
  tipo: string | null | undefined,
  final: number,
  depois: LeiturasDoTurno['depois'] | undefined,
): string | null {
  if (!depois || final <= depois.valor) return null;
  const m = medidorDo(tipo);
  return `Maior que a leitura de ${formatarAteDuasCasas(depois.valor)} ${m.unidade} registrada depois deste turno, em ${formatarDataHora(new Date(depois.lidaEm))}. Confira o ${m.noMeio} final e o turno escolhido.`;
}
